import { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Shield, Database,
  Target, Newspaper, Briefcase, Banknote, ArrowRight,
  ChevronRight, Clock, Check, Building2, Eye, Landmark
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SEOHead } from '@/components/SEOHead'
import CookieConsent from '@/components/CookieConsent'

type UseCase = {
  id: string
  title: string
  sector: string
  sectorIcon: React.ElementType
  accentColor: string
  tagline: string
  description: string
  challenge: string
  howSycekHelps: string[]
  modulesUsed: string[]
  outcome: string
  timeToResult: string
  dataPoints: string
  reportable: boolean
}

const USE_CASES: UseCase[] = [
  {
    id: 'ransomware-attribution',
    title: 'Ransomware Actor Attribution',
    sector: 'Cybersecurity / SOC',
    sectorIcon: Target,
    accentColor: '#ef4444',
    tagline: 'From an IOC to a named threat actor in under 30 minutes',
    description: 'A financial institution is hit by a ransomware variant. The SOC team has a wallet address, a C2 IP, and a ransom note. They need to attribute this to a known group within the hour to determine if a paid threat intelligence brief exists and whether law enforcement should be looped in.',
    challenge: 'Traditional attribution requires cross-referencing dark web forums, blockchain explorers, MISP feeds, and internal TTP databases — typically a multi-analyst, multi-hour effort.',
    howSycekHelps: [
      'IOC pivot on the C2 IP reveals linked domains and previous campaigns via Sycek\'s threat intelligence database',
      'Ransom note text pattern matched against known ransomware family signatures',
      'Crypto wallet clustered with previously identified wallets linked to the RansomHub group',
      'Dark web breach data confirms the stolen data is already being staged for sale',
      'AI-generated attribution report produced with MITRE ATT&CK TTP mapping',
    ],
    modulesUsed: ['Live Monitor', 'Sycek Profiler', 'Identity Correlation'],
    outcome: 'Attribution to RansomHub variant confirmed in 22 minutes. CISO briefed. Law enforcement notified with structured threat intel package.',
    timeToResult: '22 minutes',
    dataPoints: '14 IOCs correlated',
    reportable: true,
  },
  {
    id: 'beneficial-ownership',
    title: 'Beneficial Ownership Investigation',
    sector: 'AML / Compliance',
    sectorIcon: Banknote,
    accentColor: '#10b981',
    tagline: 'Unmasking shell structures across 4 jurisdictions in one session',
    description: 'An AML compliance team at a regional bank receives a high-value transaction from an entity registered in the British Virgin Islands. Standard KYC returns minimal information. They need to identify the ultimate beneficial owner (UBO) and determine if any PEP or sanctions exposure exists.',
    challenge: 'Corporate shells across offshore jurisdictions are specifically designed to resist standard database lookups. Traditional KYC tools return empty results or incomplete ownership chains.',
    howSycekHelps: [
      'Corporate entity search identifies the BVI company and surfaces a UK registered agent',
      'Cross-reference of the agent reveals 23 other shell entities under the same address',
      'Identity correlation links the registered director to a sanctioned individual via a maiden name variation',
      'Social media footprint of the director shows connections to known PEP network in Eastern Europe',
      'Full ownership map generated as a relationship graph with all source citations',
    ],
    modulesUsed: ['Sycek Profiler', 'Identity Correlation', 'Social Stream Intelligence'],
    outcome: 'UBO identified as a politically exposed person under EU sanctions. Transaction blocked. SAR filed within 48 hours.',
    timeToResult: '45 minutes',
    dataPoints: '23 entities mapped',
    reportable: true,
  },
  {
    id: 'missing-person',
    title: 'Missing Person Locate',
    sector: 'Law Enforcement',
    sectorIcon: Shield,
    accentColor: '#22c55e',
    tagline: 'Reconstructing a digital trail from a phone number alone',
    description: 'A detective working a missing persons case has one lead: a prepaid phone number last used 11 days ago. No social media accounts are known, no addresses on file. They need to build a picture of the subject and identify last known activity.',
    challenge: 'Prepaid numbers and anonymous online behavior are designed to resist investigation. Cross-platform correlation requires technical expertise most investigators don\'t have on hand.',
    howSycekHelps: [
      'Phone number enrichment reveals caller ID, carrier, and registration region',
      'Breach database lookup surfaces an email address associated with the number in a 2022 data leak',
      'Email pivot finds accounts on 3 platforms including a Reddit account with activity up to 9 days ago',
      'Reddit posting pattern shows conversations referencing a city and a specific hostel chain',
      'Facial recognition match from a tagged photo links to a VK profile with recent location posts',
    ],
    modulesUsed: ['Sycek Profiler', 'Identity Correlation', 'Facial Recognition'],
    outcome: 'Subject located at a hostel in Goa 48 hours after investigation began. Facial recognition cross-match was the decisive pivot.',
    timeToResult: '3 hours',
    dataPoints: '6 platforms linked',
    reportable: true,
  },
  {
    id: 'disinformation-campaign',
    title: 'Disinformation Network Mapping',
    sector: 'Journalism / Government',
    sectorIcon: Newspaper,
    accentColor: '#06b6d4',
    tagline: 'Tracing a coordinated inauthentic behavior campaign back to its origin',
    description: 'A news organization investigates a viral narrative that appeared simultaneously across 5 platforms within 24 hours. The story contained fabricated quotes attributed to a real politician. They need to determine whether this is organic outrage or a coordinated campaign — and who is behind it.',
    challenge: 'Coordinated campaigns disguise themselves as organic content. Identifying shared infrastructure and inauthentic coordination requires simultaneous monitoring across platforms that rarely share data.',
    howSycekHelps: [
      'Social Stream keyword monitoring captures 2,400 posts across X, Reddit, Facebook, and Telegram within the analysis window',
      'AI sentiment engine identifies 6 distinct narrative clusters all pushing identical framing',
      'Account creation date analysis reveals 340 accounts were registered within the same 48-hour window',
      'Network graph identifies 12 amplifier accounts with overlapping follower networks',
      'Telegram channel tracing links 3 channels to a known state-affiliated propaganda network',
    ],
    modulesUsed: ['Social Stream Intelligence', 'X Investigation', 'Identity Correlation'],
    outcome: 'Campaign attributed to a state-linked influence operation. Published investigation with full evidence chain. 3 Telegram channels subsequently removed.',
    timeToResult: '6 hours',
    dataPoints: '2,400 posts analyzed',
    reportable: true,
  },
  {
    id: 'executive-protection',
    title: 'Executive Digital Threat Assessment',
    sector: 'Corporate Risk',
    sectorIcon: Briefcase,
    accentColor: '#f97316',
    tagline: 'Understanding your CEO\'s threat surface before an adversary does',
    description: 'A Fortune 500 company\'s security team is onboarding a new CEO. Before their first public appearance, the team needs a full digital threat assessment — exposed personal data, dark web presence, and any hostile content directed at the executive.',
    challenge: 'Executives rarely know their own digital footprint. Credential exposure, doxxing content, and targeted threat actor mentions are scattered across platforms that require specialized tools to access.',
    howSycekHelps: [
      'Full digital footprint scan surfaces personal email, home address, and phone number in 4 data breaches',
      'Dark web search finds the CEO\'s corporate credentials in a recent leak, still unexpired',
      'Social media monitoring identifies 2 accounts running coordinated harassment campaigns',
      'Adverse media scan across 50+ languages finds no prior litigation, criminal, or reputational risk',
      'Alert policies configured to notify within 15 minutes of any new mentions in threat actor channels',
    ],
    modulesUsed: ['Sycek Profiler', 'Social Stream Intelligence', 'Identity Correlation'],
    outcome: 'Exposed credentials revoked before use. Harassment accounts reported and removed. Ongoing monitoring established with zero analyst overhead.',
    timeToResult: '2 hours',
    dataPoints: '4 breach exposures found',
    reportable: true,
  },
  {
    id: 'facility-monitoring',
    title: 'Sensitive Facility Change Detection',
    sector: 'Government & Defense',
    sectorIcon: Building2,
    accentColor: '#3b82f6',
    tagline: 'Detecting unauthorized construction via historical satellite imagery',
    description: 'A defense intelligence team monitors a suspected military installation in a denied-access region. They receive a tip about unusual activity but cannot conduct ground reconnaissance. They need to verify whether construction or equipment movement has occurred in the past 90 days.',
    challenge: 'Commercial satellite imagery platforms require manual comparison across dates. Identifying subtle changes — vehicle density, shadow patterns, ground disturbance — requires trained imagery analysts.',
    howSycekHelps: [
      'AOI pinned on target coordinates with 90-day historical imagery pulled automatically',
      'Before/after comparison reveals 3 new structures and a cleared area consistent with vehicle staging',
      'Shadow analysis estimates structure heights between 8–12 metres — consistent with aircraft hangars',
      'Temporal playback shows construction activity concentrated on weekends, suggesting operational security awareness',
      'Geolocation-linked social media posts from nearby accounts provide corroborating open-source signals',
    ],
    modulesUsed: ['GEOINT', 'Social Stream Intelligence'],
    outcome: 'Credible evidence of undisclosed construction activity confirmed. Intelligence report submitted. Imagery evidence used to support a formal monitoring escalation.',
    timeToResult: '1 hour',
    dataPoints: '90 days of imagery',
    reportable: true,
  },
  {
    id: 'insurance-fraud',
    title: 'Insurance Fraud Investigation',
    sector: 'Financial Services',
    sectorIcon: Landmark,
    accentColor: '#f59e0b',
    tagline: 'Cross-referencing a claimant\'s social media against their disability claim',
    description: 'An insurance firm investigates a high-value disability claim submitted by a claimant alleging complete immobility following a workplace injury. The claim is two years old and the claimant is now seeking a €2.4M settlement.',
    challenge: 'Claimants frequently maintain low social media profiles. Connecting an identity across multiple platforms requires both technical access and investigative methodology — skills typically absent in insurance investigation teams.',
    howSycekHelps: [
      'Identity search on claimant\'s name and email surfaces 4 active social accounts not disclosed during onboarding',
      'Timeline reconstruction shows 180+ posts since the alleged injury date, including physical activity',
      'GEOINT analysis of geotagged photos places the claimant at a sporting event 6 months after claimed onset',
      'Facial recognition confirms identity across all discovered accounts with high confidence',
      'Full evidence package generated with timestamps, source URLs, and screenshots',
    ],
    modulesUsed: ['Sycek Profiler', 'GEOINT', 'Facial Recognition', 'Identity Correlation'],
    outcome: 'Claim rejected with prejudice. Civil litigation initiated by insurer. Evidence package accepted by court. Estimated €2.4M saved.',
    timeToResult: '4 hours',
    dataPoints: '180+ posts documented',
    reportable: true,
  },
  {
    id: 'supply-chain-attack',
    title: 'Supply Chain Attack Investigation',
    sector: 'Cybersecurity / SOC',
    sectorIcon: Eye,
    accentColor: '#8b5cf6',
    tagline: 'Tracing a compromised vendor back through the attack chain',
    description: 'A tech company discovers suspicious outbound traffic to an unfamiliar IP. Initial analysis suggests the traffic originates from a third-party analytics library bundled by a SaaS vendor. The security team needs to determine the scope, origin, and whether data has been exfiltrated.',
    challenge: 'Supply chain attacks are designed to be invisible. The compromised component sits several layers away from the affected organization, making attribution and scope assessment extremely difficult without specialized tooling.',
    howSycekHelps: [
      'IP pivot reveals the destination is linked to 12 other malicious campaigns in the last 90 days',
      'Domain registration data tied to a recently formed entity with connections to known cybercrime infrastructure',
      'Dark web scan finds the SaaS vendor\'s admin credentials in a forum post from 6 months prior',
      'Social engineering indicators identified: the vendor\'s developer was targeted by a spear-phishing campaign',
      'Full attack chain mapped: credential theft → vendor compromise → library poisoning → downstream exfiltration',
    ],
    modulesUsed: ['Live Monitor', 'Identity Correlation', 'Sycek Profiler'],
    outcome: 'Attack chain fully documented. Vendor notified and library patched within 24 hours. Affected customers informed. Law enforcement referral made.',
    timeToResult: '90 minutes',
    dataPoints: '12 linked campaigns',
    reportable: true,
  },
]

const SECTORS = ['All', 'Cybersecurity / SOC', 'AML / Compliance', 'Law Enforcement', 'Journalism / Government', 'Corporate Risk', 'Government & Defense', 'Financial Services']

export default function UseCases() {
  const [activeSector, setActiveSector] = useState('All')
  const [activeCase, setActiveCase] = useState<UseCase | null>(null)

  const filtered = activeSector === 'All' ? USE_CASES : USE_CASES.filter(u => u.sector === activeSector)

  return (
    <>
      <SEOHead
        title="OSINT Use Cases — Sycek Intelligence Platform"
        description="Real-world intelligence use cases: ransomware attribution, AML investigations, missing persons, disinformation tracking, and more — powered by Sycek."
        keywords="OSINT use cases, cyber intelligence examples, ransomware attribution, AML investigation, missing persons OSINT, disinformation mapping"
        canonical="/use-cases"
      />

      <div className="min-h-screen bg-[#070b0e] text-white">

        {/* Nav */}
        <header className="sticky top-0 z-50 border-b border-white/[0.07] bg-[#070b0e]/95 backdrop-blur-md">
          <div className="container mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 rounded-lg overflow-hidden">
                <img src="/logo.jpeg" alt="Sycek" className="w-full h-full object-cover" />
              </div>
              <div>
                <span className="text-white font-bold text-sm">Sycek</span>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest leading-none">Intelligence Platform</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-2 text-sm text-slate-400">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white">Use Cases</span>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white hover:bg-white/5">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="bg-green-500 hover:bg-green-600 text-black font-bold">Get Started</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="relative py-24 overflow-hidden border-b border-white/[0.06]">
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(ellipse at 30% 50%, #22c55e30 0%, transparent 60%), radial-gradient(ellipse at 70% 20%, #3b82f620 0%, transparent 50%)' }} />
          <div className="container mx-auto px-6 relative z-10">
            <motion.div className="max-w-4xl" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Badge className="mb-5 bg-green-500/15 text-green-400 border border-green-500/30 font-mono text-[10px] tracking-widest">
                [REAL-WORLD CASE STUDIES]
              </Badge>
              <h1 className="text-5xl md:text-6xl font-bold mb-5 leading-tight">
                Intelligence in action.<br />
                <span className="text-green-400">Real scenarios. Real outcomes.</span>
              </h1>
              <p className="text-xl text-slate-400 mb-8 max-w-2xl leading-relaxed">
                Every use case below reflects investigative workflows our platform powers across sectors — from ransomware attribution to beneficial ownership tracing. No demos. No hypotheticals.
              </p>
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-slate-400">{USE_CASES.length} documented scenarios</span>
                </div>
                <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-2">
                  <Clock className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-xs text-slate-400">Avg. time to result: &lt;3 hours</span>
                </div>
                <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] rounded-full px-4 py-2">
                  <Check className="w-3.5 h-3.5 text-green-400" />
                  <span className="text-xs text-slate-400">All outcomes court-reportable</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Filter */}
        <section className="border-b border-white/[0.06] bg-black/30">
          <div className="container mx-auto px-6 py-5">
            <div className="flex flex-wrap gap-2">
              {SECTORS.map(s => (
                <button
                  key={s}
                  onClick={() => setActiveSector(s)}
                  className={`px-4 py-1.5 rounded-full text-xs font-mono transition-all ${
                    activeSector === s
                      ? 'bg-green-500 text-black font-bold'
                      : 'bg-white/[0.04] border border-white/[0.08] text-slate-400 hover:text-white hover:border-white/15'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Case cards */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-7xl mx-auto">
              {filtered.map((uc, i) => {
                const Icon = uc.sectorIcon
                return (
                  <motion.div
                    key={uc.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: (i % 3) * 0.07 }}
                    className="bg-white/[0.02] border border-white/[0.07] rounded-2xl overflow-hidden hover:border-white/15 transition-all duration-200 cursor-pointer group flex flex-col"
                    style={{ borderLeftColor: uc.accentColor, borderLeftWidth: 3 }}
                    onClick={() => setActiveCase(uc)}
                  >
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${uc.accentColor}18`, border: `1px solid ${uc.accentColor}40` }}>
                          <Icon className="w-5 h-5" style={{ color: uc.accentColor }} />
                        </div>
                        <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 rounded-full px-2.5 py-1">
                          <Clock className="w-3 h-3 text-green-400" />
                          <span className="text-green-400 text-[10px] font-mono">{uc.timeToResult}</span>
                        </div>
                      </div>

                      <Badge className="w-fit mb-3 text-[10px] font-mono" style={{ backgroundColor: `${uc.accentColor}15`, color: uc.accentColor, border: `1px solid ${uc.accentColor}30` }}>
                        {uc.sector}
                      </Badge>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-green-400 transition-colors leading-snug">{uc.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed mb-4 flex-1">{uc.tagline}</p>

                      <div className="border-t border-white/[0.06] pt-4 flex items-center justify-between">
                        <div className="flex gap-2 flex-wrap">
                          {uc.modulesUsed.slice(0, 2).map(m => (
                            <span key={m} className="text-[10px] bg-white/[0.04] border border-white/[0.08] text-slate-500 px-2 py-0.5 rounded font-mono">{m}</span>
                          ))}
                          {uc.modulesUsed.length > 2 && (
                            <span className="text-[10px] text-slate-600">+{uc.modulesUsed.length - 2}</span>
                          )}
                        </div>
                        <span className="text-xs text-slate-600 group-hover:text-green-400 transition-colors flex items-center gap-1">
                          View case <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Stats strip */}
        <section className="py-16 border-t border-white/[0.06] bg-black/40">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: '< 15 min', label: 'Avg. first pivot', color: '#22c55e' },
                { value: '8 sectors', label: 'Industry coverage', color: '#60a5fa' },
                { value: '100%', label: 'Court-reportable output', color: '#a78bfa' },
                { value: '1 platform', label: 'Replaces 9+ tools', color: '#f59e0b' },
              ].map(s => (
                <div key={s.label}>
                  <div className="text-3xl font-black font-mono mb-1" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-slate-500 text-xs uppercase tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 border-t border-white/[0.06]">
          <div className="container mx-auto px-6 text-center max-w-2xl">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to run your first investigation?</h2>
            <p className="text-slate-400 mb-8">Access all 7 intelligence modules on a single platform. No setup required.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-green-500 hover:bg-green-600 text-black font-bold px-8">
                  Get Platform Access <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="ghost" className="text-slate-300 hover:text-white border border-white/10 px-8">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Case detail modal */}
        {activeCase && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) setActiveCase(null) }}
          >
            <motion.div
              className="bg-[#0d1117] border border-white/[0.1] rounded-2xl max-w-3xl w-full my-8"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b border-white/[0.07]" style={{ borderLeftColor: activeCase.accentColor, borderLeftWidth: 3 }}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Badge className="mb-2 text-[10px] font-mono" style={{ backgroundColor: `${activeCase.accentColor}15`, color: activeCase.accentColor, border: `1px solid ${activeCase.accentColor}30` }}>
                      {activeCase.sector}
                    </Badge>
                    <h2 className="text-2xl font-bold text-white mb-1">{activeCase.title}</h2>
                    <p style={{ color: activeCase.accentColor }} className="text-sm font-medium">{activeCase.tagline}</p>
                  </div>
                  <button onClick={() => setActiveCase(null)} className="text-slate-500 hover:text-white text-xl leading-none flex-shrink-0 mt-1">✕</button>
                </div>
                <div className="flex flex-wrap gap-3 mt-4">
                  <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] rounded-full px-3 py-1">
                    <Clock className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-xs text-slate-300">{activeCase.timeToResult}</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/[0.08] rounded-full px-3 py-1">
                    <Database className="w-3.5 h-3.5 text-blue-400" />
                    <span className="text-xs text-slate-300">{activeCase.dataPoints}</span>
                  </div>
                  {activeCase.reportable && (
                    <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 rounded-full px-3 py-1">
                      <Check className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-xs text-green-400">Court-reportable</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Scenario */}
                <div>
                  <h3 className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">Scenario</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{activeCase.description}</p>
                </div>

                {/* Challenge */}
                <div className="bg-red-500/5 border border-red-500/15 rounded-xl p-4">
                  <h3 className="text-xs font-mono text-red-400 uppercase tracking-widest mb-2">The Challenge</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{activeCase.challenge}</p>
                </div>

                {/* How Sycek helps */}
                <div>
                  <h3 className="text-xs font-mono text-green-400 uppercase tracking-widest mb-3">How Sycek Solved It</h3>
                  <ul className="space-y-2.5">
                    {activeCase.howSycekHelps.map((step, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <div className="w-5 h-5 rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-green-400 text-[10px] font-bold">{i + 1}</span>
                        </div>
                        <span className="text-slate-300 leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Modules used */}
                <div>
                  <h3 className="text-xs font-mono text-slate-500 uppercase tracking-widest mb-2">Modules Used</h3>
                  <div className="flex flex-wrap gap-2">
                    {activeCase.modulesUsed.map(m => (
                      <span key={m} className="text-xs bg-white/[0.05] border border-white/[0.1] text-slate-300 px-3 py-1 rounded-full font-mono">{m}</span>
                    ))}
                  </div>
                </div>

                {/* Outcome */}
                <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4">
                  <h3 className="text-xs font-mono text-green-400 uppercase tracking-widest mb-2">Outcome</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{activeCase.outcome}</p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Link to="/register" className="flex-1">
                    <Button className="w-full bg-green-500 hover:bg-green-600 text-black font-bold">
                      Start Your Investigation <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Button variant="ghost" className="border border-white/10 text-slate-400 hover:text-white" onClick={() => setActiveCase(null)}>
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        <CookieConsent />
      </div>
    </>
  )
}
